import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Utensils } from "lucide-react";
import { upsertDailyMenu, getMenuByDate } from "@/services/products/menuService";
import { useToast } from "@/hooks/useToast";

interface MenuSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date | null;
  onMenuUpdated: () => void;
}

/**
 * Modal de selección de menú optimizado para dispositivos móviles
 *
 * Permite seleccionar productos para el menú del día con
 * interfaz táctil optimizada y feedback visual claro.
 */
export default function MenuSelectionModal({
  isOpen,
  onClose,
  selectedDate,
  onMenuUpdated,
}: MenuSelectionModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [menuName, setMenuName] = useState("");
  const [menuDescription, setMenuDescription] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const { toast } = useToast();

  // Cargar configuración existente cuando cambia la fecha
  useEffect(() => {
    if (isOpen && selectedDate) {
      loadExistingMenu();
    }
  }, [isOpen, selectedDate]);

  // Actualizar la URL de vista previa según la imagen seleccionada o la existente
  useEffect(() => {
    if (imageFile) {
      const url = URL.createObjectURL(imageFile);
      setImagePreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setImagePreviewUrl(existingImageUrl);
  }, [imageFile, existingImageUrl]);

  const loadExistingMenu = async () => {
    if (!selectedDate) return;

    try {
      setIsLoading(true);
      const menu = await getMenuByDate(selectedDate);
      setMenuName(menu?.nombre ?? `Menú ${formatDate(selectedDate)}`);
      setMenuDescription(menu?.descripcion ?? "");
      setImageFile(null);
      setExistingImageUrl(menu?.imagen_url ?? null);
    } catch (error) {
      console.error("Error al cargar menú existente:", error);
      setMenuName(selectedDate ? `Menú ${formatDate(selectedDate)}` : "");
      setMenuDescription("");
      setExistingImageUrl(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedDate) return;

    setIsSaving(true);
    try {
      await upsertDailyMenu(selectedDate, {
        nombre: menuName || `Menú ${formatDate(selectedDate)}`,
        descripcion: menuDescription || undefined,
        imageFile,
        productIds: [],
      });

      toast({
        title: "Menú guardado",
        description: `Configuración guardada para ${selectedDate.toLocaleDateString(
          "es-CL"
        )}`,
      });

      onMenuUpdated();
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo guardar la configuración del menú",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("es-CL", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Utensils className="h-5 w-5" />
            Configurar Menú del Día
          </DialogTitle>
          <DialogDescription>
            {selectedDate
              ? `Configura el menú del ${formatDate(selectedDate)}`
              : "Configura el menú del día"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Información */}
          <div className="p-4 bg-muted/50 rounded-lg border border-border">
            <h4 className="font-medium text-sm mb-3 text-foreground">
              Información del Menú
            </h4>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>• Este flujo define nombre, descripción e imagen del menú del día</p>
            </div>
          </div>

          {/* Campos de menú */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Nombre del menú</Label>
              <input
                className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
                placeholder="Ej: Menú del día"
                value={menuName}
                onChange={(e) => setMenuName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Descripción</Label>
              <input
                className="w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
                placeholder="Opcional"
                value={menuDescription}
                onChange={(e) => setMenuDescription(e.target.value)}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-sm">Imagen</Label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
              />
              {imagePreviewUrl && (
                <div className="mt-2">
                  <div className="text-xs text-muted-foreground mb-2">Vista previa</div>
                  <img
                    src={imagePreviewUrl}
                    alt="Vista previa menú"
                    className="max-h-48 w-auto rounded-md border border-border object-cover"
                  />
                </div>
              )}
            </div>
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Cargando...</span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-3 pt-4">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
            className="h-12 px-6 text-base"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !selectedDate}
            className="min-w-[140px] h-12 text-base font-medium bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              "Guardar Cambios"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
